/**
 * Alshallal Pastry Company HR System - Employee Documents Tab Component
 * Phase 14: Employee → Documents Fast Access
 * Phase 19: Added inline upload with category support including "Other"
 * Phase 25.1: Enhanced with Document Preview component
 * Phase D1: Added template-based document issuance
 * 
 * Displays documents for a specific employee with filtering and actions.
 * HR roles can view, download, approve/reject documents.
 * HR can now upload documents directly from this tab with category selection.
 * 
 * Two document actions:
 * 1. Issue Document (Template-Based) - Primary flow for generating official documents
 * 2. Upload External Document - For scanned/third-party files
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Filter,
  RefreshCw,
  Loader2,
  Upload,
  Search,
  Plus,
  FileSearch,
  FileCheck,
  FolderOpen,
  Edit2,
  Edit3,
  ArrowRight,
  ShieldAlert,
  Calendar as CalendarIcon,
  Briefcase,
  DollarSign,
  AlertOctagon,
  Lock
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import OCRProcessingDialog from '@/components/documents/OCRProcessingDialog';
import DocumentPreview from '@/components/documents/DocumentPreview';
import { sanitizeHtml } from '@/lib/sanitize';
import WarningHistoryPanel from '@/components/warnings/WarningHistoryPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Arabic letters; an English section must not take them from a typed field.
const ARABIC_TEXT = /[\u0600-\u06FF]/;

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle },
};

const STATUS_LABELS = {
  pending: { en: 'Pending', ar: 'قيد المراجعة' },
  approved: { en: 'Approved', ar: 'موافق عليه' },
  rejected: { en: 'Rejected', ar: 'مرفوض' },
};

// Document categories for employee documents
const DOCUMENT_CATEGORIES = [
  { value: 'national_id', label_en: 'National ID', label_ar: 'الهوية الوطنية' },
  { value: 'iqama', label_en: 'Iqama', label_ar: 'الإقامة' },
  { value: 'passport', label_en: 'Passport', label_ar: 'جواز السفر' },
  { value: 'contract', label_en: 'Contract', label_ar: 'العقد' },
  { value: 'health_insurance', label_en: 'Health Insurance', label_ar: 'التأمين الصحي' },
  { value: 'education_certificate', label_en: 'Education Certificate', label_ar: 'شهادة التعليم' },
  { value: 'professional_license', label_en: 'Professional License', label_ar: 'الرخصة المهنية' },
  { value: 'training_certificate', label_en: 'Training Certificate', label_ar: 'شهادة التدريب' },
  { value: 'other', label_en: 'Other', label_ar: 'أخرى' },
];

const EmployeeDocumentsTab = ({ employeeId, employeeName }) => {
  const { token, hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();

  // State
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    document_type_id: '',
    expiring_within_days: ''
  });

  // Review dialog
  const [reviewDialog, setReviewDialog] = useState({ open: false, document: null, action: null });
  const [reviewReason, setReviewReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // View dialog
  const [viewDialog, setViewDialog] = useState({ open: false, document: null });

  // Upload dialog state (Phase 19)
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    category: '',
    displayName: '',
    documentTypeId: '',
    issueDate: '',
    hasExpiry: false,  // NEW: checkbox state
    expiryDate: '',
    notes: '',
    certificateNumber: '',   // Optional; only shown for health-cert types
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // OCR Dialog state (Phase 24)
  const [ocrDialog, setOcrDialog] = useState({ open: false, document: null });
  const [lastUploadedDocument, setLastUploadedDocument] = useState(null);

  // Phase D1: Issue Document (Template-Based) state
  const [issueDialog, setIssueDialog] = useState(false);
  const [issueDocTypes, setIssueDocTypes] = useState([]);  // Template-supported types grouped by category
  const [issueCategories, setIssueCategories] = useState([]);
  const [selectedIssueType, setSelectedIssueType] = useState(null);
  const [issueTemplate, setIssueTemplate] = useState(null);
  const [issueManualFields, setIssueManualFields] = useState({});
  const [issueLanguage, setIssueLanguage] = useState('ar_en');
  const [issueLoading, setIssueLoading] = useState(false);
  const [issuePreview, setIssuePreview] = useState(null);
  // Editable content state
  const [issueContentAr, setIssueContentAr] = useState('');
  const [issueContentEn, setIssueContentEn] = useState('');
  const [showContentEditor, setShowContentEditor] = useState(false);
  // Phase D2: Impact Preview state
  const [impactPreview, setImpactPreview] = useState(null);
  const [updateMode, setUpdateMode] = useState(null);  // "auto_apply" or "require_approval"
  
  // Employee data for auto-populating fields
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeDataLoading, setEmployeeDataLoading] = useState(false);

  // RBAC
  const canReview = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');
  const canUpload = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');
  const canIssue = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');

  // Fetch employee data for auto-populating issue document fields
  const fetchEmployeeData = useCallback(async () => {
    if (!employeeId || !token) return;
    
    setEmployeeDataLoading(true);
    try {
      const response = await axios.get(`${API_URL}/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeData(response.data);
    } catch (err) {
      console.error('Failed to fetch employee data:', err);
    } finally {
      setEmployeeDataLoading(false);
    }
  }, [employeeId, token]);

  // Fetch document types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`${API_URL}/documents/types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDocumentTypes(response.data.document_types || []);
      } catch (err) {
        console.error('Failed to fetch document types:', err);
      }
    };
    fetchTypes();
  }, [token]);

  // Phase D1: Fetch template-supported document types
  useEffect(() => {
    const fetchIssueTypes = async () => {
      try {
        const response = await axios.get(`${API_URL}/document-templates/types?supports_template=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIssueDocTypes(response.data.document_types || []);
        setIssueCategories(response.data.categories || []);
      } catch (err) {
        console.error('Failed to fetch issuable document types:', err);
      }
    };
    if (canIssue) {
      fetchIssueTypes();
    }
  }, [token, canIssue]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('employee_id', employeeId);
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.document_type_id) {
        params.append('document_type_id', filters.document_type_id);
      }
      if (filters.expiring_within_days) {
        params.append('expiring_within_days', filters.expiring_within_days);
      }

      const response = await axios.get(`${API_URL}/documents/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDocuments(response.data.documents || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, employeeId, page, pageSize, filters]);

  useEffect(() => {
    if (employeeId) {
      fetchDocuments();
    }
  }, [fetchDocuments, employeeId]);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReviewSubmit = async () => {
    if (!reviewDialog.document || reviewReason.length < 10) return;

    try {
      setSubmitting(true);
      await axios.post(
        `${API_URL}/documents/${reviewDialog.document.id}/review`,
        {
          status: reviewDialog.action,
          reason: reviewReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setReviewDialog({ open: false, document: null, action: null });
      setReviewReason('');
      fetchDocuments();
    } catch (err) {
      console.error('Review failed:', err);
      setError(err.response?.data?.detail?.error || 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 19: Handle document upload directly from Employee Documents tab
  const handleUpload = async () => {
    if (!uploadFile || !uploadData.documentTypeId) return;
    
    // Validate: "other" category requires displayName
    if (uploadData.category === 'other' && !uploadData.displayName.trim()) {
      setUploadError(isRTL ? 'عنوان المستند مطلوب للفئة "أخرى"' : 'Document title is required for "Other" category');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('file', uploadFile);

      // Build query params
      const params = new URLSearchParams();
      params.append('document_type_id', uploadData.documentTypeId);
      
      if (uploadData.category) {
        params.append('category', uploadData.category);
      }
      if (uploadData.displayName.trim()) {
        params.append('display_name', uploadData.displayName.trim());
      }
      if (uploadData.issueDate) {
        params.append('issue_date', uploadData.issueDate);
      }
      // Only send expiry_date if checkbox is checked
      if (uploadData.hasExpiry && uploadData.expiryDate) {
        params.append('expiry_date', uploadData.expiryDate);
      }
      if (uploadData.notes.trim()) {
        params.append('notes', uploadData.notes.trim());
      }
      // Certificate number is optional. Only send when the user actually
      // provided a value AND the selected type is a health / fitness
      // certificate (otherwise it's meaningless on the record).
      const selectedTypeMeta = documentTypes.find(x => x.id === uploadData.documentTypeId);
      const isHealthCertType = !!selectedTypeMeta && (
        selectedTypeMeta.code === 'FITNESS_CERTIFICATE' ||
        selectedTypeMeta.name === 'Fitness Certificate' ||
        selectedTypeMeta.name_ar === 'شهادة اللياقة الصحية'
      );
      if (isHealthCertType && uploadData.certificateNumber.trim()) {
        params.append('certificate_number', uploadData.certificateNumber.trim());
      }

      const response = await axios.post(
        `${API_URL}/documents/upload/${employeeId}?${params.toString()}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Get the uploaded document info
      const uploadedDoc = response.data;

      // Reset and close upload dialog
      setUploadDialog(false);
      setUploadData({
        category: '',
        displayName: '',
        documentTypeId: '',
        issueDate: '',
        hasExpiry: false,
        expiryDate: '',
        notes: '',
        certificateNumber: '',
      });
      setUploadFile(null);
      
      // Refresh documents list
      await fetchDocuments();

      // Phase 24: Show OCR prompt after successful upload
      if (uploadedDoc?.id || uploadedDoc?.document?.id) {
        const docId = uploadedDoc.id || uploadedDoc.document?.id;
        setLastUploadedDocument({ id: docId, ...uploadedDoc });
        setOcrDialog({ open: true, document: { id: docId, ...uploadedDoc } });
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err.response?.data?.detail?.error || err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadDialog = () => {
    setUploadDialog(false);
    setUploadData({
      category: '',
      displayName: '',
      documentTypeId: '',
      issueDate: '',
      hasExpiry: false,
      expiryDate: '',
      notes: '',
      certificateNumber: '',
    });
    setUploadFile(null);
    setUploadError(null);
  };

  // Helper: is the currently-selected document type a health / fitness
  // certificate? Used to surface the optional certificate_number field on
  // the upload dialog. Robust across old (name-only) and new (code) rows.
  const _selectedUploadTypeMeta = documentTypes.find(x => x.id === uploadData.documentTypeId);
  const _isHealthCertUploadType = !!_selectedUploadTypeMeta && (
    _selectedUploadTypeMeta.code === 'FITNESS_CERTIFICATE' ||
    _selectedUploadTypeMeta.name === 'Fitness Certificate' ||
    _selectedUploadTypeMeta.name_ar === 'شهادة اللياقة الصحية'
  );

  // Phase D1: Issue Document handlers
  const handleOpenIssueDialog = async () => {
    setIssueDialog(true);
    setSelectedIssueType(null);
    setIssueTemplate(null);
    setIssueManualFields({});
    setIssuePreview(null);
    
    // Fetch employee data for auto-populating fields
    await fetchEmployeeData();
  };

  // Helper function to get auto-populated value from employee data
  const getAutoPopulatedValue = (fieldKey, empData) => {
    if (!empData) return '';
    
    // Map field keys to employee data paths
    const fieldMapping = {
      // Current employee data (read-only)
      'current_position': empData.employment?.job_title || empData.job_title || '',
      'current_job_title': empData.employment?.job_title || empData.job_title || '',
      'current_department': empData.employment?.department || empData.department || '',
      'current_salary': empData.compensation?.basic_salary || empData.salary || '',
      'current_basic_salary': empData.compensation?.basic_salary || empData.salary || '',
      'current_housing': empData.compensation?.housing_allowance || empData.housing_allowance || '',
      'current_housing_allowance': empData.compensation?.housing_allowance || empData.housing_allowance || '',
      'current_transport': empData.compensation?.transportation_allowance || empData.transport_allowance || '',
      'current_transport_allowance': empData.compensation?.transportation_allowance || empData.transport_allowance || '',
      'current_gross': (() => {
        const basic = parseFloat(empData.compensation?.basic_salary || empData.salary || 0);
        const housing = parseFloat(empData.compensation?.housing_allowance || empData.housing_allowance || 0);
        const transport = parseFloat(empData.compensation?.transportation_allowance || empData.transport_allowance || 0);
        const other = parseFloat(empData.compensation?.other_allowances || empData.other_allowances || 0);
        return basic + housing + transport + other || '';
      })(),
      // Employee identifiers (read-only)
      'employee_name': empData.personal?.first_name ? `${empData.personal.first_name} ${empData.personal.last_name}` : empData.first_name ? `${empData.first_name} ${empData.last_name}` : '',
      'employee_name_ar': empData.personal?.first_name_ar ? `${empData.personal.first_name_ar} ${empData.personal.last_name_ar}` : empData.first_name_ar ? `${empData.first_name_ar} ${empData.last_name_ar}` : '',
      'employee_number': empData.employee_number || empData.employment?.employee_number || '',
      'national_id': empData.identity?.national_id || empData.national_id || '',
      'iqama_no': empData.identity?.iqama_number || empData.iqama_number || '',
      'iqama_number': empData.identity?.iqama_number || empData.iqama_number || '',
      'passport_no': empData.identity?.passport_number || empData.passport_number || '',
      'hire_date': empData.employment?.hire_date || empData.hire_date || '',
      'join_date': empData.employment?.hire_date || empData.hire_date || '',
    };
    
    return fieldMapping[fieldKey] || '';
  };

  // Check if a field should be auto-populated (read-only)
  const isAutoPopulatedField = (fieldKey) => {
    const autoPopulatedPrefixes = [
      'current_', 
      'employee_name', 
      'employee_number',
      'national_id',
      'iqama_',
      'passport_',
      'hire_date',
      'join_date'
    ];
    return autoPopulatedPrefixes.some(prefix => fieldKey.startsWith(prefix) || fieldKey === prefix);
  };

  const handleSelectIssueType = async (typeCode) => {
    setSelectedIssueType(typeCode);
    setIssueTemplate(null);
    setIssueManualFields({});
    setIssuePreview(null);
    setIssueLoading(true);

    try {
      // Fetch the published template for this document type
      const response = await axios.get(
        `${API_URL}/document-templates/by-type/${typeCode}?status=published`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIssueTemplate(response.data);
      
      // Initialize manual fields - auto-populate "current_" fields from employee data
      const fields = {};
      (response.data.manual_fields || []).forEach(field => {
        if (isAutoPopulatedField(field.key)) {
          // Auto-populate from employee data (read-only)
          fields[field.key] = getAutoPopulatedValue(field.key, employeeData);
        } else {
          // Leave editable fields empty
          fields[field.key] = '';
        }
      });
      setIssueManualFields(fields);
      
      // Initialize editable content from template
      setIssueContentAr(response.data.content_ar || '');
      setIssueContentEn(response.data.content_en || '');
      setShowContentEditor(false); // Start collapsed
      
      // Reset impact preview
      setImpactPreview(null);
      setUpdateMode(null);
    } catch (err) {
      console.error('Failed to fetch template:', err);
      const errorMsg = err.response?.data?.detail?.message || 
                       err.response?.data?.detail?.error ||
                       (isRTL ? 'لا يوجد قالب منشور لهذا النوع' : 'No published template exists for this document type');
      toast.error(errorMsg);
      setSelectedIssueType(null);
    } finally {
      setIssueLoading(false);
    }
  };

  // Phase D2: Fetch impact preview when manual fields change
  const fetchImpactPreview = async () => {
    if (!selectedIssueType || !employeeId) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/documents/validate-issuance/${employeeId}`,
        {
          document_type_code: selectedIssueType,
          manual_payload: issueManualFields
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.has_effects) {
        setImpactPreview(response.data.impact_preview || []);
        setUpdateMode(response.data.mode);
      } else {
        setImpactPreview(null);
        setUpdateMode(null);
      }
    } catch (err) {
      // If validation fails due to missing fields, that's okay - clear preview
      setImpactPreview(null);
      setUpdateMode(null);
    }
  };

  // Helper function to group changes by domain
  const groupChangesByDomain = (changes) => {
    if (!changes || changes.length === 0) return {};
    
    const groups = {
      employment: { 
        label_en: 'Employment', 
        label_ar: 'بيانات التوظيف',
        icon: Briefcase,
        fields: ['job_title', 'job_title_ar', 'department', 'department_ar', 'department_id', 'branch_id', 'manager_id', 'status', 'contract_end_date', 'termination_date'],
        changes: []
      },
      compensation: { 
        label_en: 'Compensation', 
        label_ar: 'التعويضات',
        icon: DollarSign,
        fields: ['salary', 'housing_allowance', 'transport_allowance', 'other_allowances'],
        changes: []
      },
      disciplinary: { 
        label_en: 'Disciplinary', 
        label_ar: 'تأديبي',
        icon: AlertOctagon,
        fields: [],  // Deductions that are marked as disciplinary
        changes: []
      },
      banking: { 
        label_en: 'Banking', 
        label_ar: 'البيانات البنكية',
        icon: ShieldAlert,
        fields: ['bank_name', 'iban'],
        changes: []
      }
    };
    
    changes.forEach(change => {
      const field = change.field;
      let assigned = false;
      
      // Check if it's a deduction (disciplinary)
      if (change.is_deduction) {
        groups.disciplinary.changes.push(change);
        assigned = true;
      }
      
      if (!assigned) {
        for (const [groupKey, group] of Object.entries(groups)) {
          if (group.fields.includes(field)) {
            group.changes.push(change);
            assigned = true;
            break;
          }
        }
      }
      
      // Default to employment if not matched
      if (!assigned) {
        groups.employment.changes.push(change);
      }
    });
    
    // Filter out empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, group]) => group.changes.length > 0)
    );
  };

  // Format value for display
  const formatChangeValue = (value, field) => {
    if (value === null || value === undefined || value === '') {
      return isRTL ? '(فارغ)' : '(empty)';
    }
    // Format currency fields
    if (['salary', 'housing_allowance', 'transport_allowance', 'other_allowances'].includes(field)) {
      return `${Number(value).toLocaleString()} SAR`;
    }
    // Format date fields
    if (['contract_end_date', 'termination_date'].includes(field)) {
      try {
        return format(new Date(value), 'PP');
      } catch {
        return value;
      }
    }
    return String(value);
  };

  // Debounced effect to fetch impact preview
  useEffect(() => {
    if (selectedIssueType && Object.values(issueManualFields).some(v => v)) {
      const timer = setTimeout(fetchImpactPreview, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedIssueType, issueManualFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIssueDocument = async () => {
    if (!selectedIssueType || !issueTemplate) return;

    // Validate required manual fields
    const requiredFields = (issueTemplate.manual_fields || []).filter(f => f.required);
    for (const field of requiredFields) {
      if (!issueManualFields[field.key]) {
        toast.error(isRTL 
          ? `الحقل "${field.label_ar || field.label_en}" مطلوب` 
          : `Field "${field.label_en || field.label_ar}" is required`);
        return;
      }
    }

    setIssueLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/documents/issue/${employeeId}`,
        {
          document_type_code: selectedIssueType,
          language: issueLanguage,
          manual_payload: issueManualFields,
          // Send edited content if user modified it
          content_ar: issueContentAr,
          content_en: issueContentEn
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Show success message with update info if applicable
      let successMessage = isRTL 
        ? `تم إصدار المستند بنجاح: ${response.data.reference_no}` 
        : `Document issued successfully: ${response.data.reference_no}`;
      
      if (response.data.employee_updates) {
        const updates = response.data.employee_updates;
        if (updates.applied) {
          toast.success(successMessage);
          toast.info(isRTL 
            ? `تم تطبيق ${updates.changes?.length || 0} تحديث على ملف الموظف` 
            : `Applied ${updates.changes?.length || 0} update(s) to employee profile`);
        } else if (updates.pending_update_id) {
          toast.success(successMessage);
          toast.warning(isRTL 
            ? 'التحديثات في انتظار موافقة مدير الموارد البشرية' 
            : 'Updates pending HR Manager approval');
        } else if (updates.scheduled_update_id) {
          toast.success(successMessage);
          toast.info(isRTL 
            ? 'التحديثات مجدولة للتطبيق في تاريخ السريان' 
            : 'Updates scheduled for effective date');
        } else {
          toast.success(successMessage);
        }
      } else {
        toast.success(successMessage);
      }
      
      setIssueDialog(false);
      setSelectedIssueType(null);
      setIssueTemplate(null);
      setIssueManualFields({});
      setImpactPreview(null);
      setUpdateMode(null);
      fetchDocuments();
    } catch (err) {
      console.error('Failed to issue document:', err);
      const errorDetail = err.response?.data?.detail;
      const statusCode = err.response?.status;
      
      // Handle different error codes with descriptive messages
      if (errorDetail?.code === 'MISSING_REQUIRED_FIELDS') {
        const missingLabels = errorDetail.missing_fields.map(f => 
          isRTL ? f.label_ar : f.label_en
        ).join(', ');
        toast.error(isRTL 
          ? `الحقول التالية مطلوبة: ${missingLabels}` 
          : `Required fields missing: ${missingLabels}`);
      } else if (errorDetail?.code === 'MISSING_EFFECT_FIELDS') {
        // No Partial Updates Rule - must provide all effect fields
        const missingLabels = errorDetail.missing_fields?.map(f => 
          isRTL ? f.label_ar : f.label_en
        ).join(', ') || '';
        toast.error(isRTL 
          ? `الحقول التالية مطلوبة لتحديث ملف الموظف: ${missingLabels}. لا يمكن إصدار المستند بدونها.` 
          : `The following fields are required for employee update: ${missingLabels}. Document cannot be issued without them.`);
      } else if (errorDetail?.code === 'TEMPLATE_NOT_FOUND') {
        toast.error(isRTL 
          ? `لا يوجد قالب منشور لنوع المستند: ${errorDetail.document_type_name_ar || errorDetail.document_type_code}. يرجى إنشاء ونشر قالب أولاً.`
          : `No published template exists for: ${errorDetail.document_type_name_en || errorDetail.document_type_code}. Please create and publish a template first.`);
      } else if (errorDetail?.code === 'PDF_GENERATION_FAILED') {
        toast.error(isRTL 
          ? `فشل إنشاء ملف PDF: ${errorDetail.error}`
          : `PDF generation failed: ${errorDetail.error}`);
      } else if (errorDetail?.code === 'EMPLOYEE_NOT_FOUND') {
        toast.error(isRTL 
          ? 'لم يتم العثور على الموظف'
          : 'Employee not found');
      } else if (errorDetail?.code === 'PERMISSION_DENIED') {
        toast.error(isRTL 
          ? 'ليس لديك صلاحية لإصدار هذا المستند'
          : 'You do not have permission to issue this document');
      } else if (statusCode === 500) {
        // Server error - show the actual error message
        const errorMsg = errorDetail?.error || (typeof errorDetail === 'string' ? errorDetail : 'Internal server error');
        toast.error(isRTL 
          ? `خطأ في الخادم: ${errorMsg}`
          : `Server error: ${errorMsg}`);
      } else {
        // Generic error with actual message
        const errorMsg = errorDetail?.error || errorDetail?.message || 
          (typeof errorDetail === 'string' ? errorDetail : 'Failed to issue document');
        toast.error(isRTL 
          ? `فشل إصدار المستند: ${errorMsg}`
          : `Failed to issue document: ${errorMsg}`);
      }
    } finally {
      setIssueLoading(false);
    }
  };

  const handlePreviewIssue = async () => {
    if (!issueTemplate) return;

    setIssueLoading(true);
    try {
      // Preview with actual employee data
      const response = await axios.post(
        `${API_URL}/document-templates/${issueTemplate.id}/preview-employee/${employeeId}`,
        { manual_payload: issueManualFields, language: issueLanguage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIssuePreview(response.data);
    } catch (err) {
      console.error('Failed to preview:', err);
      toast.error(isRTL ? 'فشل تحميل المعاينة' : 'Failed to load preview');
    } finally {
      setIssueLoading(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await axios.get(
        `${API_URL}/documents/${document.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.file_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      document_type_id: '',
      expiring_within_days: ''
    });
    setPage(1);
  };

  const getStatusLabel = (status) => {
    const labels = STATUS_LABELS[status];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  // Expiry helpers — trust the backend's `is_expired` and `days_until_expiry`
  // fields whenever they are present. Backend computes them with date-only
  // semantics (uses `date.today()`), so a document does not appear expired
  // until the calendar day AFTER its expiry date. Frontend re-computation
  // from raw date strings is unsafe because `new Date("YYYY-MM-DD")` is
  // parsed as UTC midnight, which can falsely flip a same-day expiry to
  // "expired" depending on the user's timezone.
  const isExpired = (doc) => {
    if (!doc) return false;
    if (typeof doc.is_expired === 'boolean') return doc.is_expired;
    // Fallback for legacy callers passing just the date string
    const ed = typeof doc === 'string' ? doc : doc.expiry_date;
    if (!ed) return false;
    const [y, m, d] = ed.split('-').map(Number);
    if (!y || !m || !d) return false;
    const today = new Date();
    const todayYMD = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    const expiryYMD = y * 10000 + m * 100 + d;
    return expiryYMD < todayYMD;
  };

  const isExpiringSoon = (doc) => {
    if (!doc) return false;
    if (typeof doc.days_until_expiry === 'number') {
      return doc.days_until_expiry >= 0 && doc.days_until_expiry <= 30;
    }
    const ed = typeof doc === 'string' ? doc : doc.expiry_date;
    if (!ed) return false;
    const [y, m, d] = ed.split('-').map(Number);
    if (!y || !m || !d) return false;
    const today = new Date();
    const todayYMD = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    const expiryYMD = y * 10000 + m * 100 + d;
    if (expiryYMD < todayYMD) return false;
    // count whole days
    const todayD = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const expiryD = new Date(y, m - 1, d);
    const days = Math.round((expiryD - todayD) / 86400000);
    return days >= 0 && days <= 30;
  };

  const hasActiveFilters = filters.status !== 'all' || filters.document_type_id || filters.expiring_within_days;

  // Stats
  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const approvedCount = documents.filter(d => d.status === 'approved').length;
  const expiredCount = documents.filter(d => isExpired(d)).length;

  return (
    <div className="space-y-4" data-testid="employee-documents-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-2xl font-bold text-slate-900">{total}</div>
          <div className="text-sm text-slate-600">{isRTL ? 'إجمالي المستندات' : 'Total Documents'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
          <div className="text-sm text-slate-600">{isRTL ? 'قيد المراجعة' : 'Pending Review'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600">{approvedCount}</div>
          <div className="text-sm text-slate-600">{isRTL ? 'موافق عليه' : 'Approved'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-red-600">{expiredCount}</div>
          <div className="text-sm text-slate-600">{isRTL ? 'منتهي الصلاحية' : 'Expired'}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {isRTL ? 'تصفية المستندات' : 'Filter Documents'}
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              {/* Phase D1: Issue Document (Template-Based) - Primary Action */}
              {canIssue && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleOpenIssueDialog}
                  data-testid="issue-document-btn"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <FileCheck className="h-4 w-4 me-2" />
                  {isRTL ? 'إصدار مستند' : 'Issue Document'}
                </Button>
              )}
              {/* Upload External Document - Secondary Action */}
              {canUpload && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUploadDialog(true)}
                  data-testid="upload-document-btn"
                >
                  <Upload className="h-4 w-4 me-2" />
                  {isRTL ? 'رفع مستند خارجي' : 'Upload External'}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={fetchDocuments} disabled={loading}>
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'الحالة' : 'Status'}</Label>
              <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="pending">{isRTL ? 'قيد المراجعة' : 'Pending'}</SelectItem>
                  <SelectItem value="approved">{isRTL ? 'موافق عليه' : 'Approved'}</SelectItem>
                  <SelectItem value="rejected">{isRTL ? 'مرفوض' : 'Rejected'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Document Type Filter */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'نوع المستند' : 'Document Type'}</Label>
              <Select
                value={filters.document_type_id}
                onValueChange={(v) => handleFilterChange('document_type_id', v === 'all' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All Types'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الأنواع' : 'All Types'}</SelectItem>
                  {documentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Expiry Filter */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'انتهاء الصلاحية' : 'Expiring Within'}</Label>
              <Select
                value={filters.expiring_within_days}
                onValueChange={(v) => handleFilterChange('expiring_within_days', v === 'all' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'أي وقت' : 'Any time'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'أي وقت' : 'Any time'}</SelectItem>
                  <SelectItem value="0">{isRTL ? 'منتهي الصلاحية' : 'Expired'}</SelectItem>
                  <SelectItem value="7">{isRTL ? '7 أيام' : '7 days'}</SelectItem>
                  <SelectItem value="30">{isRTL ? '30 يوم' : '30 days'}</SelectItem>
                  <SelectItem value="60">{isRTL ? '60 يوم' : '60 days'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">
            ×
          </button>
        </div>
      )}

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد مستندات' : 'No Documents'}
              </h3>
              <p className="text-slate-600 mb-4">
                {isRTL ? 'لم يتم رفع أي مستندات لهذا الموظف' : 'No documents uploaded for this employee'}
              </p>
              {canUpload && (
                <Button onClick={() => navigate(`/documents?upload_for=${employeeId}`)}>
                  <Upload className="h-4 w-4 me-2" />
                  {isRTL ? 'رفع مستند' : 'Upload Document'}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'نوع المستند' : 'Document Type'}</TableHead>
                  <TableHead>{isRTL ? 'اسم الملف' : 'File Name'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ الرفع' : 'Uploaded'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ الانتهاء' : 'Expiry'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => {
                  const StatusIcon = STATUS_STYLES[doc.status]?.icon || Clock;
                  const expired = isExpired(doc);
                  const expiringSoon = isExpiringSoon(doc);

                  return (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <span>{doc.document_type_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{doc.file_name}</TableCell>
                      <TableCell className="text-slate-600">{formatDate(doc.uploaded_at)}</TableCell>
                      <TableCell>
                        {doc.expiry_date ? (
                          <div className="flex items-center gap-2">
                            {expired && <AlertTriangle className="h-4 w-4 text-red-500" />}
                            {expiringSoon && !expired && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                            <span className={cn(
                              expired && "text-red-600 font-medium",
                              expiringSoon && !expired && "text-amber-600 font-medium"
                            )}>
                              {formatDate(doc.expiry_date)}
                            </span>
                          </div>
                        ) : doc.requires_expiry ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs gap-1" data-testid={`no-expiry-badge-${doc.id}`}>
                            <AlertTriangle className="h-3 w-3" />
                            {isRTL ? 'لا يوجد تاريخ انتهاء' : 'No expiry set'}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_STYLES[doc.status]?.bg}>
                          <StatusIcon className="h-3 w-3 me-1" />
                          {getStatusLabel(doc.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewDialog({ open: true, document: doc })}
                            title={isRTL ? 'عرض' : 'View'}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                            title={isRTL ? 'تحميل' : 'Download'}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {/* Phase 24: Apply OCR button for existing documents */}
                          {canReview && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => setOcrDialog({ open: true, document: doc })}
                              title={isRTL ? 'تطبيق OCR' : 'Apply OCR'}
                            >
                              <FileSearch className="h-4 w-4" />
                            </Button>
                          )}
                          {canReview && doc.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => setReviewDialog({ open: true, document: doc, action: 'approved' })}
                                title={isRTL ? 'موافقة' : 'Approve'}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setReviewDialog({ open: true, document: doc, action: 'rejected' })}
                                title={isRTL ? 'رفض' : 'Reject'}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-slate-600">
                {isRTL
                  ? `${total} مستند`
                  : `${total} documents`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {isRTL ? 'السابق' : 'Previous'}
                </Button>
                <span className="text-sm text-slate-600">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {isRTL ? 'التالي' : 'Next'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => !open && setReviewDialog({ open: false, document: null, action: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={reviewDialog.action === 'approved' ? 'text-green-600' : 'text-red-600'}>
              {reviewDialog.action === 'approved'
                ? (isRTL ? 'اعتماد المستند' : 'Approve Document')
                : (isRTL ? 'رفض المستند' : 'Reject Document')}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.document && (
                <span>{reviewDialog.document.document_type_name} - {reviewDialog.document.file_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                {reviewDialog.action === 'approved'
                  ? (isRTL ? 'سبب الموافقة (مطلوب)' : 'Reason for approval (required)')
                  : (isRTL ? 'سبب الرفض (مطلوب)' : 'Reason for rejection (required)')}
              </Label>
              <Textarea
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder={isRTL ? 'أدخل السبب (10 أحرف على الأقل)...' : 'Enter reason (at least 10 characters)...'}
                rows={4}
              />
              {reviewReason.length > 0 && reviewReason.length < 10 && (
                <p className="text-sm text-red-500">
                  {isRTL ? `${10 - reviewReason.length} أحرف متبقية` : `${10 - reviewReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, document: null, action: null })}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleReviewSubmit}
              disabled={reviewReason.length < 10 || submitting}
              className={reviewDialog.action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('loading')}</>
              ) : (
                reviewDialog.action === 'approved' ? (isRTL ? 'اعتماد' : 'Approve') : (isRTL ? 'رفض' : 'Reject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview - Phase 25.1: Enhanced preview with print/save */}
      <DocumentPreview
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, document: null })}
        document={viewDialog.document}
        token={token}
        isRTL={isRTL}
        documentType="employee"
      />

      {/* Phase 19: Upload Document Dialog */}
      <Dialog open={uploadDialog} onOpenChange={(open) => !open && resetUploadDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {isRTL ? 'رفع مستند للموظف' : 'Upload Document for Employee'}
            </DialogTitle>
            <DialogDescription>
              {employeeName && (
                <span className="font-medium">{employeeName}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {uploadError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {uploadError}
              </div>
            )}

            {/* Document Type */}
            <div className="space-y-2">
              <Label htmlFor="doc-type">
                {isRTL ? 'نوع المستند' : 'Document Type'} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={uploadData.documentTypeId}
                onValueChange={(v) => setUploadData(prev => ({ ...prev, documentTypeId: v }))}
              >
                <SelectTrigger id="doc-type" data-testid="upload-doc-type">
                  <SelectValue placeholder={isRTL ? 'اختر نوع المستند' : 'Select document type'} />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="doc-category">
                {isRTL ? 'الفئة' : 'Category'}
              </Label>
              <Select
                value={uploadData.category}
                onValueChange={(v) => setUploadData(prev => ({ ...prev, category: v, displayName: v === 'other' ? prev.displayName : '' }))}
              >
                <SelectTrigger id="doc-category" data-testid="upload-category">
                  <SelectValue placeholder={isRTL ? 'اختر الفئة (اختياري)' : 'Select category (optional)'} />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {language === 'ar' ? cat.label_ar : cat.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Display Name - Required for "Other" category */}
            {uploadData.category === 'other' && (
              <div className="space-y-2">
                <Label htmlFor="display-name">
                  {isRTL ? 'عنوان المستند' : 'Document Title'} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="display-name"
                  value={uploadData.displayName}
                  onChange={(e) => setUploadData(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder={isRTL ? 'أدخل عنوان المستند' : 'Enter document title'}
                  data-testid="upload-display-name"
                />
                <p className="text-xs text-slate-500">
                  {isRTL ? 'مطلوب عند اختيار فئة "أخرى"' : 'Required when "Other" category is selected'}
                </p>
              </div>
            )}

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="file-upload">
                {isRTL ? 'الملف' : 'File'} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="file-upload"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="upload-file-input"
              />
              <p className="text-xs text-slate-500">
                {isRTL ? 'PDF, صور, أو مستندات Word (حد أقصى 10MB)' : 'PDF, images, or Word documents (max 10MB)'}
              </p>
            </div>

            {/* Issue Date */}
            <div className="space-y-2">
              <Label htmlFor="issue-date">{isRTL ? 'تاريخ الإصدار' : 'Issue Date'}</Label>
              <Input
                id="issue-date"
                type="date"
                value={uploadData.issueDate}
                onChange={(e) => setUploadData(prev => ({ ...prev, issueDate: e.target.value }))}
                data-testid="upload-issue-date"
              />
            </div>

            {/* Expiry Date Checkbox + Conditional Field */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <Checkbox
                  id="has-expiry"
                  checked={uploadData.hasExpiry}
                  onCheckedChange={(checked) => setUploadData(prev => ({ 
                    ...prev, 
                    hasExpiry: checked,
                    expiryDate: checked ? prev.expiryDate : ''  // Clear date if unchecked
                  }))}
                  data-testid="has-expiry-checkbox"
                />
                <Label 
                  htmlFor="has-expiry" 
                  className="text-sm font-medium cursor-pointer"
                >
                  {isRTL ? 'هذا المستند له تاريخ انتهاء' : 'This document has an expiry date'}
                </Label>
              </div>
              
              {uploadData.hasExpiry && (
                <div className="space-y-2 pl-6 rtl:pl-0 rtl:pr-6">
                  <Label htmlFor="expiry-date">{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'} *</Label>
                  <Input
                    id="expiry-date"
                    type="date"
                    value={uploadData.expiryDate}
                    onChange={(e) => setUploadData(prev => ({ ...prev, expiryDate: e.target.value }))}
                    data-testid="upload-expiry-date"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {isRTL 
                      ? 'سيتم تتبع هذا المستند في تنبيهات انتهاء الصلاحية'
                      : 'This document will be tracked in expiry alerts'}
                  </p>
                </div>
              )}
            </div>

            {/* Certificate number — health-cert types only. Fully optional. */}
            {_isHealthCertUploadType && (
              <div className="space-y-2" data-testid="upload-cert-number-block">
                <Label htmlFor="certificate-number">
                  {isRTL ? 'رقم الشهادة' : 'Certificate Number'}
                  <span className="ml-2 text-xs text-slate-500">
                    ({isRTL ? 'اختياري' : 'optional'})
                  </span>
                </Label>
                <Input
                  id="certificate-number"
                  value={uploadData.certificateNumber}
                  onChange={(e) => setUploadData(prev => ({
                    ...prev,
                    certificateNumber: e.target.value,
                  }))}
                  placeholder={isRTL ? 'أدخل رقم الشهادة' : 'Enter certificate number'}
                  maxLength={128}
                  data-testid="upload-certificate-number"
                />
                <p className="text-xs text-muted-foreground">
                  {isRTL
                    ? 'حقل اختياري خاص بشهادة اللياقة الصحية — لا يمنع الحفظ إذا تُرك فارغاً.'
                    : 'Optional field for the Fitness Certificate — leaving it blank does not block saving.'}
                </p>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                id="notes"
                value={uploadData.notes}
                onChange={(e) => setUploadData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={isRTL ? 'ملاحظات إضافية (اختياري)' : 'Additional notes (optional)'}
                rows={2}
                data-testid="upload-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetUploadDialog} disabled={uploading}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={
                uploading || 
                !uploadFile || 
                !uploadData.documentTypeId ||
                (uploadData.category === 'other' && !uploadData.displayName.trim()) ||
                (uploadData.hasExpiry && !uploadData.expiryDate)  // Require expiry date if checkbox checked
              }
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {isRTL ? 'جاري الرفع...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 me-2" />
                  {isRTL ? 'رفع' : 'Upload'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase D1: Issue Document (Template-Based) Dialog */}
      <Dialog 
        open={issueDialog} 
        onOpenChange={(open) => {
          setIssueDialog(open);
          if (!open) {
            // Cleanup state when dialog closes
            setSelectedIssueType(null);
            setIssueTemplate(null);
            setIssueManualFields({});
            setIssuePreview(null);
            setImpactPreview(null);
            setUpdateMode(null);
            setShowContentEditor(false);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-emerald-600" />
              {isRTL ? 'إصدار مستند من قالب' : 'Issue Document from Template'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'اختر نوع المستند واملأ الحقول المطلوبة لإصدار المستند' 
                : 'Select document type and fill required fields to issue the document'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4 space-y-4">
            {/* Document Type Selection */}
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع المستند' : 'Document Type'} *</Label>
              <Select
                value={selectedIssueType || ''}
                onValueChange={handleSelectIssueType}
                disabled={issueLoading}
              >
                <SelectTrigger data-testid="issue-doc-type-select">
                  <FolderOpen className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={isRTL ? 'اختر نوع المستند' : 'Select document type'} />
                </SelectTrigger>
                <SelectContent>
                  {issueCategories.map(cat => (
                    <div key={cat.code}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase bg-slate-50">
                        {isRTL ? cat.name_ar : cat.name_en}
                      </div>
                      {issueDocTypes
                        .filter(dt => dt.category === cat.code)
                        .map(dt => (
                          <SelectItem key={dt.code} value={dt.code}>
                            {isRTL ? dt.name_ar : dt.name_en}
                          </SelectItem>
                        ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Template Loading */}
            {issueLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            )}

            {/* Template Found - Show Fields */}
            {issueTemplate && !issueLoading && (
              <>
                {/* Template Info */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="h-4 w-4" />
                    <span className="font-medium">
                      {isRTL ? 'قالب منشور متاح' : 'Published template available'}
                    </span>
                    <Badge variant="outline" className="ml-auto">
                      v{issueTemplate.version}
                    </Badge>
                  </div>
                </div>

                {/* Language Selection */}
                <div className="space-y-2">
                  <Label>{isRTL ? 'لغة المستند' : 'Document Language'}</Label>
                  <Select value={issueLanguage} onValueChange={setIssueLanguage}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ar">{isRTL ? 'العربية فقط' : 'Arabic Only'}</SelectItem>
                      <SelectItem value="en">{isRTL ? 'الإنجليزية فقط' : 'English Only'}</SelectItem>
                      <SelectItem value="ar_en">{isRTL ? 'ثنائي اللغة' : 'Bilingual'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Editable Document Content */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">{isRTL ? 'محتوى المستند' : 'Document Content'}</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowContentEditor(!showContentEditor)}
                      data-testid="toggle-content-editor"
                    >
                      <Edit2 className="h-4 w-4 me-2" />
                      {showContentEditor 
                        ? (isRTL ? 'إخفاء المحرر' : 'Hide Editor') 
                        : (isRTL ? 'تعديل المحتوى' : 'Edit Content')}
                    </Button>
                  </div>
                  
                  {showContentEditor && (
                    <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                      <div className="text-xs text-slate-500 mb-2">
                        {isRTL 
                          ? 'يمكنك تعديل المحتوى قبل الإصدار. المتغيرات {{placeholder}} سيتم استبدالها تلقائياً.' 
                          : 'You can edit the content before issuing. Placeholders like {{placeholder}} will be auto-replaced.'}
                      </div>
                      
                      {/* Arabic Content */}
                      <div className="space-y-1">
                        <Label className="text-sm">{isRTL ? 'المحتوى العربي' : 'Arabic Content'}</Label>
                        <Textarea
                          value={issueContentAr}
                          onChange={(e) => setIssueContentAr(e.target.value)}
                          className="min-h-[120px] font-arabic"
                          dir="rtl"
                          placeholder={isRTL ? 'المحتوى العربي...' : 'Arabic content...'}
                          data-testid="issue-content-ar"
                        />
                      </div>
                      
                      {/* English Content */}
                      <div className="space-y-1">
                        <Label className="text-sm">{isRTL ? 'المحتوى الإنجليزي' : 'English Content'}</Label>
                        <Textarea
                          value={issueContentEn}
                          onChange={(e) => setIssueContentEn(e.target.value)}
                          className="min-h-[120px]"
                          placeholder={isRTL ? 'المحتوى الإنجليزي...' : 'English content...'}
                          data-testid="issue-content-en"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Manual Fields */}
                {issueTemplate.manual_fields && issueTemplate.manual_fields.length > 0 && (
                  <div className="space-y-4">
                    {/* Auto-populated fields (Current Employee Data - Read Only) */}
                    {issueTemplate.manual_fields.some(f => isAutoPopulatedField(f.key)) && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Lock className="h-4 w-4 text-slate-500" />
                          <Label className="text-base font-semibold text-slate-700">
                            {isRTL ? 'البيانات الحالية (تلقائية)' : 'Current Data (Auto-populated)'}
                          </Label>
                        </div>
                        <p className="text-xs text-slate-500">
                          {isRTL 
                            ? 'هذه الحقول مستخرجة من ملف الموظف ولا يمكن تعديلها من هنا' 
                            : 'These fields are extracted from employee profile and cannot be edited here'}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          {issueTemplate.manual_fields.filter(f => isAutoPopulatedField(f.key)).map(field => (
                            <div key={field.key} className="space-y-1">
                              <Label className="text-sm text-slate-600">
                                {isRTL ? field.label_ar : field.label_en}
                              </Label>
                              <Input
                                value={issueManualFields[field.key] || '—'}
                                disabled
                                className="bg-slate-100 text-slate-700 cursor-not-allowed border-slate-200"
                                data-testid={`issue-field-${field.key}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Warning letters: the employee's warnings before issuing a new one, and the one-time deduction */}
                    {selectedIssueType === 'WARNING_LETTER' && (
                      <WarningHistoryPanel employeeId={employeeId} token={token} isRTL={isRTL}
                        testid="issue-warning-history"
                        levelOptions={(issueTemplate.manual_fields.find((f) => f.key === 'warning_level') || {}).options || []}
                        selectedLevel={issueManualFields.warning_level}
                        onPickLevel={(v) => setIssueManualFields((cur) => ({ ...cur, warning_level: v }))}
                        values={issueManualFields}
                        onChange={(k, v) => setIssueManualFields((cur) => { const next = { ...cur }; if (v === undefined) delete next[k]; else next[k] = v; return next; })} />
                    )}

                    {/* Editable fields (New Values) */}
                    {issueTemplate.manual_fields.some(f => !isAutoPopulatedField(f.key)) && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Edit3 className="h-4 w-4 text-blue-600" />
                          <Label className="text-base font-semibold text-blue-700">
                            {isRTL ? 'القيم الجديدة / التغييرات' : 'New Values / Changes'}
                          </Label>
                        </div>
                        <p className="text-xs text-blue-600">
                          {isRTL 
                            ? 'أدخل القيم الجديدة التي سيتم تطبيقها عند إصدار المستند' 
                            : 'Enter the new values that will be applied when the document is issued'}
                        </p>
                        <div className="space-y-3">
                          {issueTemplate.manual_fields.filter(f => !isAutoPopulatedField(f.key)).map(field => {
                            const fieldType = field.field_type || field.type;
                            const needsEnglish = issueLanguage !== 'ar' && ['text', 'textarea'].includes(fieldType || 'text')
                              && ARABIC_TEXT.test(issueManualFields[field.key] || '');
                            return (
                            <div key={field.key} className="space-y-1">
                              <Label className="text-sm">
                                {isRTL ? field.label_ar : field.label_en}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </Label>
                              {fieldType === 'select' ? (
                                <Select
                                  value={issueManualFields[field.key] || undefined}
                                  onValueChange={(v) => setIssueManualFields(prev => ({ ...prev, [field.key]: v }))}
                                >
                                  <SelectTrigger data-testid={`issue-field-${field.key}`}>
                                    <SelectValue placeholder={isRTL ? 'اختر' : 'Choose'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(field.options || []).map(o => (
                                      <SelectItem key={o.value} value={o.value}>{(isRTL ? o.label_ar : o.label_en) || o.value}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : fieldType === 'textarea' ? (
                                <Textarea
                                  value={issueManualFields[field.key] || ''}
                                  onChange={(e) => setIssueManualFields(prev => ({
                                    ...prev,
                                    [field.key]: e.target.value
                                  }))}
                                  placeholder={isRTL ? field.label_ar : field.label_en}
                                  rows={3}
                                  data-testid={`issue-field-${field.key}`}
                                />
                              ) : fieldType === 'date' || field.key.includes('date') || field.key.includes('effective') ? (
                                <Input
                                  type="date"
                                  value={issueManualFields[field.key] || ''}
                                  onChange={(e) => setIssueManualFields(prev => ({
                                    ...prev,
                                    [field.key]: e.target.value
                                  }))}
                                  className="w-full"
                                  data-testid={`issue-field-${field.key}`}
                                />
                              ) : fieldType === 'number' || fieldType === 'currency' || field.key.includes('salary') || field.key.includes('gross') ? (
                                <Input
                                  type="number"
                                  value={issueManualFields[field.key] || ''}
                                  onChange={(e) => setIssueManualFields(prev => ({
                                    ...prev,
                                    [field.key]: e.target.value
                                  }))}
                                  placeholder={field.type === 'currency' || field.key.includes('salary') ? '0.00' : ''}
                                  data-testid={`issue-field-${field.key}`}
                                />
                              ) : (
                                <Input
                                  value={issueManualFields[field.key] || ''}
                                  onChange={(e) => setIssueManualFields(prev => ({
                                    ...prev,
                                    [field.key]: e.target.value
                                  }))}
                                  placeholder={isRTL ? field.label_ar : field.label_en}
                                  data-testid={`issue-field-${field.key}`}
                                />
                              )}
                              {/* The English section must stay English: Arabic text needs an English version. */}
                              {needsEnglish && (
                                <div className="space-y-1 ps-3 border-s-2 border-blue-200">
                                  <Label className="text-xs text-blue-700">
                                    {isRTL ? `${field.label_ar || field.key} (بالإنجليزية)` : `${field.label_en || field.key} (English)`}
                                    <span className="text-red-500 ml-1">*</span>
                                  </Label>
                                  {fieldType === 'textarea' ? (
                                    <Textarea
                                      dir="ltr"
                                      rows={3}
                                      value={issueManualFields[`${field.key}_en`] || ''}
                                      onChange={(e) => setIssueManualFields(prev => ({ ...prev, [`${field.key}_en`]: e.target.value }))}
                                      data-testid={`issue-field-${field.key}_en`}
                                    />
                                  ) : (
                                    <Input
                                      dir="ltr"
                                      value={issueManualFields[`${field.key}_en`] || ''}
                                      onChange={(e) => setIssueManualFields(prev => ({ ...prev, [`${field.key}_en`]: e.target.value }))}
                                      data-testid={`issue-field-${field.key}_en`}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Phase D2: Impact Preview Section - Employee Profile Updates */}
                {impactPreview && impactPreview.length > 0 && (
                  <div className="space-y-3" data-testid="impact-preview-section">
                    {/* Header Banner */}
                    <div className={cn(
                      "border rounded-lg p-4",
                      updateMode === 'require_approval' 
                        ? "bg-amber-50 border-amber-300" 
                        : "bg-blue-50 border-blue-200"
                    )}>
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "p-2 rounded-full",
                          updateMode === 'require_approval' 
                            ? "bg-amber-100" 
                            : "bg-blue-100"
                        )}>
                          {updateMode === 'require_approval' 
                            ? <ShieldAlert className="h-5 w-5 text-amber-600" />
                            : <CheckCircle className="h-5 w-5 text-blue-600" />
                          }
                        </div>
                        <div className="flex-1">
                          <h4 className={cn(
                            "font-semibold text-base",
                            updateMode === 'require_approval' 
                              ? "text-amber-800" 
                              : "text-blue-800"
                          )}>
                            {isRTL 
                              ? 'هذا المستند سيحدث ملف الموظف' 
                              : 'This document will update employee profile'}
                          </h4>
                          <p className={cn(
                            "text-sm mt-1",
                            updateMode === 'require_approval' 
                              ? "text-amber-700" 
                              : "text-blue-700"
                          )}>
                            {updateMode === 'require_approval'
                              ? (isRTL 
                                  ? 'يتطلب موافقة مدير الموارد البشرية' 
                                  : 'Requires HR Manager approval')
                              : (isRTL 
                                  ? 'سيتم تطبيق التغييرات تلقائياً عند الإصدار' 
                                  : 'Changes will be applied automatically on issuance')
                            }
                          </p>
                        </div>
                      </div>
                      
                      {/* Mode B Warning Banner */}
                      {updateMode === 'require_approval' && (
                        <div className="mt-3 flex items-center gap-2 p-2 bg-amber-100 rounded text-amber-800 text-sm">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                          <span>
                            {isRTL 
                              ? 'التغييرات ذات المخاطر العالية - سيتم إنشاء طلب موافقة' 
                              : 'High-risk changes - An approval request will be created'}
                          </span>
                        </div>
                      )}
                      
                      {/* Future Date Banner */}
                      {issueManualFields.effective_date && new Date(issueManualFields.effective_date) > new Date() && (
                        <div className="mt-3 flex items-center gap-2 p-2 bg-slate-100 rounded text-slate-700 text-sm">
                          <Calendar className="h-4 w-4 flex-shrink-0" />
                          <span>
                            {isRTL 
                              ? `مجدول للتطبيق في: ${format(new Date(issueManualFields.effective_date), 'PP')}` 
                              : `Scheduled for: ${format(new Date(issueManualFields.effective_date), 'PP')}`}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Grouped Changes Display */}
                    <div className="border rounded-lg divide-y bg-white">
                      {Object.entries(groupChangesByDomain(impactPreview)).map(([groupKey, group]) => {
                        const GroupIcon = group.icon;
                        return (
                          <div key={groupKey} className="p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <GroupIcon className="h-4 w-4 text-slate-500" />
                              <h5 className="font-medium text-slate-700">
                                {isRTL ? group.label_ar : group.label_en}
                              </h5>
                            </div>
                            <div className="space-y-2">
                              {group.changes.map((change, idx) => (
                                <div 
                                  key={`${change.field}-${idx}`}
                                  className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-sm"
                                  data-testid={`impact-change-${change.field}`}
                                >
                                  <span className="font-medium text-slate-600">
                                    {isRTL ? change.label_ar : change.label_en}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-500 line-through">
                                      {formatChangeValue(change.old_value, change.field)}
                                    </span>
                                    <ArrowRight className="h-4 w-4 text-slate-400" />
                                    <span className={cn(
                                      "font-semibold",
                                      change.is_deduction ? "text-red-600" : "text-emerald-600"
                                    )}>
                                      {formatChangeValue(change.new_value, change.field)}
                                      {change.is_deduction && (
                                        <span className="ml-1 text-xs">
                                          ({isRTL ? 'خصم' : 'deduction'})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Summary count */}
                    <p className="text-xs text-slate-500 text-center">
                      {isRTL 
                        ? `${impactPreview.length} تغيير(ات) ستطبق على ملف الموظف` 
                        : `${impactPreview.length} change(s) will be applied to employee profile`}
                    </p>
                  </div>
                )}

                {/* Preview Section */}
                {issuePreview && (
                  <div className="space-y-2">
                    <Label className="text-base">{isRTL ? 'معاينة المستند' : 'Document Preview'}</Label>
                    <div className="border rounded-lg p-4 bg-white max-h-[300px] overflow-auto">
                      <div 
                        translate="no"
                        className="prose max-w-none text-sm notranslate"
                        dir={issueLanguage === 'en' ? 'ltr' : 'rtl'}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(issueLanguage === 'ar' 
                            ? issuePreview.preview_ar 
                            : issueLanguage === 'en' 
                              ? issuePreview.preview_en
                              : `${issuePreview.preview_ar}<hr class="my-4"/>${issuePreview.preview_en}`) }} 
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIssueDialog(false)}
              disabled={issueLoading}
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            {issueTemplate && (
              <Button
                variant="outline"
                onClick={handlePreviewIssue}
                disabled={issueLoading}
              >
                <Eye className="h-4 w-4 me-2" />
                {isRTL ? 'معاينة' : 'Preview'}
              </Button>
            )}
            <Button
              onClick={handleIssueDocument}
              disabled={issueLoading || !issueTemplate}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="issue-document-submit"
            >
              {issueLoading ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <FileCheck className="h-4 w-4 me-2" />
              )}
              {isRTL ? 'إصدار المستند' : 'Issue Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 24: OCR Processing Dialog */}
      <OCRProcessingDialog
        open={ocrDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setOcrDialog({ open: false, document: null });
            setLastUploadedDocument(null);
          }
        }}
        document={ocrDialog.document}
        token={token}
        isRTL={isRTL}
        onComplete={(acceptedFields) => {
          // Refresh documents after OCR completion
          fetchDocuments();
        }}
        onSkip={() => {
          setOcrDialog({ open: false, document: null });
          setLastUploadedDocument(null);
        }}
      />
    </div>
  );
};

export default EmployeeDocumentsTab;
